import * as Mapbox from 'mapbox-gl'
import { EMPTY, forkJoin, Observable, of, ReplaySubject, Subscription } from 'rxjs'
import { filter, map, switchMap, takeUntil, tap } from 'rxjs/operators'

import { AfterViewChecked, AfterViewInit, Component, ElementRef, OnDestroy, QueryList, ViewChild, ViewChildren } from '@angular/core'
import { UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms'
import { Asset } from '@classes/Asset'
import { Feature } from '@classes/Feature'
import { FeatureProperty } from '@classes/FeatureProperty'
import { Interaction } from '@classes/Interaction'
import { CreateInteractionComponent } from '@modal/create-interaction/create-interaction.component'
import { EditDetailsComponent } from '@modal/edit-details/edit-details.component'
import { AssetService } from '@services/asset.service'
import { CommandService } from '@services/command.service'
import { CoordinatesClipboard, CopyService } from '@services/copy.service'
import { EnvironmentManagerService } from '@services/environment-manager.service'
import { AwsService } from '@services/aws.service'
import { FeatureService } from '@services/feature.service'
import { FileReferenceService } from '@services/file-reference.service'
import { InteractionService } from '@services/interaction.service'
import { LoadingService } from '@services/loading.service'
import { ModalService } from '@services/modal.service'
import { ModelService } from '@services/model.service'
import { ProjectService } from '@services/project.service'
import { SceneService } from '@services/scene.service'
import { SpatialAnnotationService } from '@services/spatial-annotation.service'
import { TooltipService } from '@services/tooltip.service'
import { UndoService } from '@services/undo.service'
import {
  AnchorMode,
  canSelectCentered,
  canSelectCRS,
  canSelectDefault,
  hasAnchorMetadata,
  resolveMapPosition,
  resolveScenePosition,
} from '@utils/anchors'

export type UpdateOptions = {
  toast?: boolean
  undoable?: boolean
}

@Component({
  selector: 'shared-edit-feature',
  templateUrl: './edit-feature.component.html',
  styleUrls: ['./edit-feature.component.css'],
  standalone: false
})
export class EditFeatureComponent implements OnDestroy, AfterViewChecked, AfterViewInit {
  @ViewChildren('checkboxes') checkboxes: QueryList<ElementRef>
  @ViewChild('fileInput') fileInput: ElementRef

  private _subscriptions: Subscription[] = []
  public axes: string[] = ['x', 'y', 'z']
  public clipboardCoordinates: [number, number][] = []
  public edited: any
  public form: UntypedFormGroup
  public selectedFeature$ = this.featureService.selectedFeature$.pipe(
    filter(feature => feature != null),
    map(feature => new Feature(feature.sceneID, feature.name, feature.type, feature)),
  )
  public showVector: Object = { position: "", rotation: "", scale: "" }
  public tab: string = "Details"
  public vectors: string[] = ['position', 'rotation', 'scale']
  public tooltips = {
    correctSeam: "Corrects any image stitching issues by overlapping the image a small amount",
  }
  private _destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>()
  public selectedFeaturesInteractions$: Observable<Interaction[]> = this.featureService.selectedFeature$.pipe(
    takeUntil(this._destroyed$),
    switchMap(feat => {
      if (!feat || !feat.interactions) return of([])
      return of(feat.interactions)
    })
  )
  public featuresAsset: Asset
  public selectedFeature: Feature
  public unloaded: boolean
  public backdrop: boolean = false

  // Anchor-mode helpers exposed to the template. Logic mirrors the model-manager
  // editor — see frontend/src/app/utilities/anchors.ts. Map scenes only expose
  // CRS + Custom (the geographic anchor + a free placement); scene-space
  // scenes (Standard / Virtual Tour) expose the full set with CRS shown but
  // disabled — CRS is a Map-only concept.
  public canSelectCentered = canSelectCentered
  public canSelectDefault = canSelectDefault
  public canSelectCRS = canSelectCRS
  public hasAnchorMetadata = hasAnchorMetadata

  get type(): string { return this.form.get('type').value }
  get properties() { return this.form.get('properties') as UntypedFormArray }
  get position() { return this.form.get('position') as UntypedFormArray }
  get rotation() { return this.form.get('rotation') as UntypedFormArray }
  get scale() { return this.form.get('scale') as UntypedFormArray }
  get attachments() { return this.properties.controls.filter(c => c.get('key').value == "attachment") }
  get map() { return this.envManager.map }
  get isBackdrop() { return this.selectedFeature?.isBackdrop }
  get isPointCloud() { return this.selectedFeature?.model?.type === "point-cloud" }
  get isMap() { return this.sceneService.selectedScene.type == 'Map' }
  get correctSeam() { return this.specificPropertyGroup('correctSeam') }
  get coordinateString() { return this.specificPropertyGroup('coordinateString') }
  get transformControls() { return this.envManager.sceneManager.transformControls }
  get canUpdateAsset() {
    // Ensure there's a single asset available
    if (!this.featuresAsset) return false

    // Find all location fields for the asset
    const locationFields = this.featuresAsset.fields.filter(field => field.type === 'location')
    if (locationFields.length !== 1) return false

    // Retrieve the location field and its corresponding value
    const locationField = locationFields[0]
    const locationFieldValue = this.featuresAsset.fieldValues.find(value => String(value.assetFieldID) === String(locationField.id))
    if (!locationFieldValue || !locationFieldValue.value) return false

    // Compare the current location to the selected feature's position
    const assetPosition = locationFieldValue.value
    const selectedPosition = this.selectedFeature.position
    const isPositionDifferent =
      assetPosition[0] !== selectedPosition[0] ||
      assetPosition[1] !== selectedPosition[1]

    return isPositionDifferent
  }

  public specificPropertyGroup(key: string) { return this.properties.controls.find(c => c.get("key").value == key) as UntypedFormGroup }
  /* For badge displaying opacity slider value */
  public isFocused(elementID: string): boolean { return document.activeElement.id == elementID }

  public updateFeature(feature: Feature, options: Partial<UpdateOptions> = {}) {
    options.undoable = options?.undoable ?? true
    options.toast = options?.toast ?? true

    if (this.unloaded != null) feature.unloaded = this.unloaded

    if (options?.undoable) {
      this._commandService.update.feature(feature).subscribe()
    } else {
      this.featureService.updateFeature(feature, { toast: options?.toast }).subscribe()
    }
  }

  public updateProperty(property: FeatureProperty, options: Partial<UpdateOptions> = {}) {
    options.undoable = options?.undoable ?? true
    options.toast = options?.toast ?? true

    if (property.key == "showOnTop") this.envManager.sceneManager.setShowOnTop(property.featureID, property.value)

    this.featureService.updateFeatureProperty(property, options).subscribe()
  }

  public updateColor(property: FeatureProperty, color: Event) {
    property.value = color
    this.featureService.updateFeatureProperty(property).subscribe()
  }

  /** When updating a Feature's `interactable` property, set interact effects to nothing */
  public updateInteractability(feature: Feature) {
    feature.onClick = 'nothing'
    feature.onHover = 'nothing'

    this.updateFeature(feature)
  }

  /**
   * Used when updating a Feature's `onHover` or `onClick` effect.
   * Sets the value based on the Feature's type. 
   */
  public updateOnInteract(feature: Feature, key: 'onClick' | 'onHover', checked: boolean) {
    if (checked == false) {
      feature[key] = 'nothing'
    } else if (feature.type == 'polygon') {
      feature[key] = 'change opacity'
    } else if (feature.type == 'line' || this.isPointCloud) {
      feature[key] = 'enlarge'
    } else {
      feature[key] = 'outline'
    }

    this.updateFeature(feature)
  }

  trackByKey(property: any): string {
    return property.key
  }

  constructor(
    private _assetService: AssetService,
    private _awsService: AwsService,
    private _commandService: CommandService,
    private _formBuilder: UntypedFormBuilder,
    private _copyService: CopyService,
    public modelService: ModelService,
    public copyService: CopyService,
    public envManager: EnvironmentManagerService,
    public featureService: FeatureService,
    public fileReferenceService: FileReferenceService,
    public interactionService: InteractionService,
    public modalService: ModalService,
    public projectService: ProjectService,
    public sceneService: SceneService,
    public tooltipService: TooltipService,
    public undoService: UndoService,
    private _annotationService: SpatialAnnotationService,
    private _loadingService: LoadingService,
  ) {
    this.form = this._formBuilder.group({
      id: [''],
      sceneID: [''],
      modelID: [''],
      model: [''],
      name: ['', Validators.required],
      type: [''],
      description: [''],
      position: this.getVectorForm(),
      rotation: this.getVectorForm(),
      scale: this.getVectorForm(),
      objectOfInterest: [''],
      visible: [''],
      opacity: [1],
      onHover: [''],
      filterable: [''],
      interactable: [''],
      lastChanged: [''],
      properties: this._formBuilder.array([])
    })

    this._subscriptions.push(
      this.featureService.selectedFeature$.pipe(
        filter(feature => feature !== undefined),
        switchMap((feature: Feature) => {
          this.featuresAsset = null

          let topLevelAttributes = {
            id: feature.id,
            sceneID: feature.sceneID,
            modelID: feature.modelID,
            model: feature.model,
            name: feature.name,
            type: feature.type,
            description: feature.description,
            objectOfInterest: feature.objectOfInterest,
            onHover: feature.onHover,
            visible: feature.visible,
            opacity: feature.opacity,
            filterable: feature.filterable,
            interactable: feature.interactable,
            lastChanged: new Date(feature.lastChanged).toDateString()
          }

          this.form.patchValue(topLevelAttributes)
          this.form.get('position').setValue(feature.position)
          this.form.get('rotation').setValue(feature.rotation)
          this.form.get('scale').setValue(feature.scale)
          this.form.setControl('properties', this._formBuilder.array([]))
          feature.properties.forEach(property => {
            let propertyForm = this._formBuilder.group({
              id: [],
              featureID: [],
              fileReferenceID: [],
              type: [],
              key: [],
              value: []
            })

            propertyForm.patchValue(property)

            if (property.fileReferenceID) {
              propertyForm.addControl("fileReference", this.getFileReferenceForm())
              propertyForm.get("fileReference").patchValue(property.fileReference)
            }
            this.properties.push(propertyForm)
          })
          let featureGroup = this.envManager.sceneManager.getFeatureGroup(feature.id)
          this.unloaded = !featureGroup
          this.selectedFeature = feature

          const assetProperty = feature.properties.find(property => property.key == 'assetID')

          if (assetProperty != null) {
            const assetID = +assetProperty.value

            return this._assetService.getAsset(assetID).pipe(tap(asset => this.featuresAsset = asset))
          } else {
            return EMPTY
          }
        })
      ).subscribe()
    )
  }

  ngAfterViewInit(): void {
    this.tooltipService.intializeTooltips()
  }

  ngAfterViewChecked(): void {
    if (!this.selectedFeature) return

    if (this.selectedFeature.type == 'group') {
      const children = this.featureService.getDescendants(this.selectedFeature).filter(f => f.type != 'group')
      const keys = ['onHover', 'filterable', 'interactable', 'objectOfInterest', 'visible']

      keys.forEach(key => {
        if (children.every(f => f[key])) this.setChecked(key)
        else if (children.every(f => !f[key])) this.setUnchecked(key)
        else this.setIndeterminate(key)
      })
    }
  }

  ngOnDestroy(): void {
    this._subscriptions.forEach(subscription => subscription.unsubscribe())
  }

  getVectorForm() {
    return this._formBuilder.array([
      this._formBuilder.control(''), // x
      this._formBuilder.control(''), // y
      this._formBuilder.control('')  // z
    ])
  }

  getFileReferenceForm() {
    return this._formBuilder.group({
      id: [],
      modelID: [],
      filename: [],
      hash: [],
      extension: []
    })
  }

  toggleVectorFocus(element, key: 'position' | 'rotation' | 'scale') {
    this.showVector[key] = this.showVector[key] == "" ? element.id : ""
  }

  toggleEdit(key: string, group: UntypedFormGroup | UntypedFormArray, isProperty: boolean = true) {
    // Check to see if there is an edited value, if not, save it for later
    if (!this.edited) {
      if (['objectOfInterest', 'visible', 'interactable', 'onHover'].includes(key)) {
        const selectedFeature = this.featureService.selectedFeature

        for (const key in group.value) selectedFeature[key] = group.value[key]

        const feature = new Feature(
          group.value.sceneID,
          group.value.name,
          group.value.type,
          selectedFeature
        )

        this._commandService.update.feature(feature).pipe(switchMap(() => {
          if (key == 'visible') {
            let scene = this.sceneService.selectedScene
            scene.updateThumbnail = true
            return this.sceneService.updateScene(scene)
          }
          return EMPTY
        })).subscribe()
      } else this.edited = group.value

    } else { // This means you are clicking out of the box and want to update values

      // Check if input is valid, if so, do it
      if (group.get(key).valid) {
        if (this.vectors.includes(key)) {
          for (let axis of (group.get(key) as UntypedFormArray).controls) {
            if (axis.value == "" || !axis.value) {
              axis.setValue(0)
            }
          }
        }
        const selectedFeature = this.featureService.selectedFeature

        if (!isProperty) for (const key in group.value) selectedFeature[key] = group.value[key]

        const feature = new Feature(
          group.value.sceneID,
          group.value.name,
          group.value.type,
          selectedFeature
        )

        const subscription: Observable<any | FeatureProperty> = isProperty ? this.featureService.updateFeatureProperty(group.value) : this._commandService.update.feature(feature)
        subscription.pipe(
          switchMap(() => {
            // If turning off trackCamera or scaleWithCamera, put it back to starting orientation
            if (isProperty && ["trackCamera", "scaleWithCamera"].includes(group.get("key").value) && group.get("value").value == "false") {
              const feature = this.selectedFeature
              const group = this.envManager.sceneManager.getFeatureGroup(feature.id)
              this.transformControls.detach()
              this.envManager.sceneManager.updateOrientation(group, feature)
              this.transformControls.attach(group)
            }

            this.edited = null

            if (this.vectors.includes(key)) {
              let scene = this.sceneService.selectedScene
              scene.updateThumbnail = true
              return this.sceneService.updateScene(scene)
            }
            return EMPTY
          })
        ).subscribe()
      } else { // This means the input was invalid and needs to be filled with what was there before
        group.get(key).setValue(this.edited)
        this.edited = null
      }
    }
  }

  /**
   * Updates a single attribute of the Selected Feature.
   * @param form the form that was edited
   * @param key the attribute of the Feature to update
   * @param undoable whether or not the update appears on the undo stack. Default `true`
   */
  updateAttribute(form: UntypedFormControl, key: string, undoable: boolean = true) {
    const feature = this.featureService.selectedFeature

    feature[key] = form.value

    if (undoable) this._commandService.update.feature(feature).subscribe()
    else this.featureService.updateFeature(feature).subscribe()
  }

  toggleBooleanProperty(key: string) {
    this.edited = this.specificPropertyGroup(key).get("value").value
    this.specificPropertyGroup(key).get('value').setValue(this.edited == "true" ? "false" : "true")
    this.toggleEdit('value', this.specificPropertyGroup(key))
  }

  editVector(event, key: string) {
    // Position change while anchored via centered/default/crs flips the cloud
    // to 'custom' mode without snapping it to the previously-saved custom
    // anchor. We deliberately leave model.custom_anchor untouched so
    // hasUncommittedCustomAnchor returns true and the commit button surfaces.
    if (key === 'position' && this._shouldAutoSwitchToCustomOnMove()) {
      this._autoSwitchToCustomAtPosition(event.vector)
      return
    }
    this.edited = event.vector
    this.toggleEdit(key, this.form, false)
  }

  private _shouldAutoSwitchToCustomOnMove(): boolean {
    if (!this.isPointCloud) return false
    const feature = this.featureService.selectedFeature
    const model = feature?.model
    if (!model || !hasAnchorMetadata(model)) return false
    return model.anchor_mode === 'centered'
      || model.anchor_mode === 'default'
      || model.anchor_mode === 'crs'
  }

  private _autoSwitchToCustomAtPosition(newPosition: [number, number, number]) {
    const feature = this.featureService.selectedFeature
    if (!feature?.model) return

    feature.position = newPosition
    feature.model.anchor_mode = 'custom'

    const group = this.envManager.sceneManager.getFeatureGroup(feature.id)
    if (group) {
      this.envManager.sceneManager.updateOrientation(group, feature)
    }

    this.featureService.selectedFeature = feature
    this._commandService.update.feature(feature).subscribe()
    this.modelService.updateModel(feature.model, { updateLocally: true }).subscribe()
  }

  editCoordinateString(coordinateString: string) {
    this.coordinateString.get('value').setValue(coordinateString)
    this.featureService.updateFeatureProperty(this.coordinateString.value).subscribe(() => {
      this.featureService.featurePositions.set(this.featureService.selectedFeature.id, JSON.parse(coordinateString))
    })
  }

  /**
   * @param key Must be the name of a variable in a Feature which is of type boolean.
   */
  updateChildren(key: string, event: PointerEvent) {
    const parent = this.featureService.selectedFeature

    if (parent.type != 'group') return

    const children: Feature[] = this.featureService.getDescendants(parent).filter(f => f.type != 'group')
    const element = event.currentTarget as HTMLInputElement
    let value

    if (element.type == 'checkbox') value = element.checked
    else if (element.type == 'radio') value = element.value

    children.forEach(child => child[key] = value)
    parent[key] = value

    forkJoin(
      children.map(f => this.featureService.updateFeature(f))
        .concat(this.featureService.updateFeature(parent))
    ).subscribe()
  }

  setIndeterminate(id: string) {
    let el = this.checkboxes.find(el => el.nativeElement.id == id)
    if (!el) return

    el.nativeElement.indeterminate = true
    el.nativeElement.checked = true
  }

  setUnchecked(id: string) {
    let el = this.checkboxes.find(el => el.nativeElement.id == id)
    if (!el) return

    el.nativeElement.indeterminate = false
    el.nativeElement.checked = false
  }

  setChecked(id: string) {
    let el = this.checkboxes.find(el => el.nativeElement.id == id)
    if (!el) return

    el.nativeElement.indeterminate = false
    el.nativeElement.checked = true
  }

  createInteraction() {
    let name = this.selectedFeature.name
    this.modalService.showAsModal(CreateInteractionComponent, { destroyOnClose: false }).then(componentRef => {
      componentRef.instance.title = name.charAt(0).toUpperCase() + name.slice(1) + "'s interactions"
    })
  }

  editDescription() {
    this.modalService.showAsModal<EditDetailsComponent>(EditDetailsComponent)
  }

  // Switch the point-cloud's anchor mode and reposition the feature.
  // In a Map scene, Centered/Default/CRS all map to anchor_centering_origin_wgs84
  // (the cloud's true geographic anchor) — there's only one map-meaningful place
  // for a centered/default/CRS cloud to sit. In scene-space scenes (VT / Standard)
  // the modes diverge: centered → [0,0,0], default → centering_origin, custom →
  // custom_anchor[0..2]. CRS isn't selectable off-map (resolveScenePosition has
  // no CRS branch). Custom uses custom_anchor[0..2] in either case — degrees on
  // a map, scene-space meters elsewhere. Model-level state (anchor_mode +
  // custom_anchor) is persisted on the Model so the choice survives reloads.
  //
  // We deliberately bypass the form-driven toggleEdit path here. toggleEdit
  // rebuilds the Feature from form.value.model, which is a stale snapshot patched
  // on the last selectedFeature$ emission — our model.anchor_mode mutation
  // never reaches it, so the re-emit would snap the radios back to 'centered'.
  setAnchorMode(feature: Feature, mode: AnchorMode) {
    const model = feature?.model
    if (!model || model.anchor_mode === mode) return

    model.anchor_mode = mode

    // Resolve placement through the shared resolver so this path stays in
    // lockstep with feature-creation (create-feature / model-menu). We
    // deliberately do NOT seed custom_anchor on first switch to 'custom' —
    // that would silently bind Custom to whatever mode the user came from
    // and make later toggles a no-op. With custom_anchor null the resolver
    // returns null and we leave feature.position alone; the commit button
    // surfaces via hasUncommittedCustomAnchor.
    const target = this.isMap ? resolveMapPosition(model) : resolveScenePosition(model)
    if (target) {
      feature.position = target
    }

    // Move the THREE.Group + re-seat transform controls synchronously. The
    // async PUT → features$ → _threeFeatures$.pairwise → updateOrientation
    // path is unreliable here: it's racy with rapid clicks, and pairwise
    // skips the update entirely when prev.equals(curr) is true — which
    // happens whenever centered↔default↔crs share the same wgs84 anchor.
    // updateOrientation handles detach/attach of the transform controls
    // internally, so the gizmo follows the cloud.
    const group = this.envManager.sceneManager.getFeatureGroup(feature.id)
    if (group) {
      this.envManager.sceneManager.updateOrientation(group, feature)
    }

    // Force BehaviorSubject emit so the radios re-render even when position
    // didn't change (the features.set equality check ignores anchor_mode).
    this.featureService.selectedFeature = feature

    // Persist position on the feature and anchor_mode/custom_anchor on the model.
    this.featureService.updateFeature(feature, { toast: false }).subscribe()
    this.modelService.updateModel(model, { updateLocally: true }).subscribe()
  }

  // True when the feature has moved since the saved custom_anchor — drives
  // the "Update Custom Anchor Position" button visibility.
  hasUncommittedCustomAnchor(feature: Feature): boolean {
    const model = feature?.model
    if (!model || model.anchor_mode !== 'custom') return false
    if (!feature.position) return false
    if (!model.custom_anchor || model.custom_anchor[0] == null) return true
    // Map scenes: position is lng/lat/alt — degrees on XY (1e-6 ≈ 0.1m), m on Z.
    // Scene-space scenes: position is meters on all three axes (1e-3 = 1mm).
    const xyTolerance = this.isMap ? 1e-6 : 1e-3
    return Math.abs(feature.position[0] - model.custom_anchor[0]) > xyTolerance
      || Math.abs(feature.position[1] - model.custom_anchor[1]) > xyTolerance
      || Math.abs(feature.position[2] - model.custom_anchor[2]) > 1e-3
  }

  // Persist the current feature position as the model's custom anchor.
  commitCustomAnchor(feature: Feature) {
    const model = feature?.model
    if (!model || !feature.position) return
    const [x, y, z] = feature.position
    const prev3 = model.custom_anchor?.[3] ?? 0
    const prev4 = model.custom_anchor?.[4] ?? 0
    model.custom_anchor = [x, y, z, prev3, prev4]
    this.modelService.updateModel(model, { updateLocally: true }).subscribe()
  }

  groundFeature() {
    const position = this.position.value as number[]
    const [longitude, latitude] = position
    const altitude = this.map.queryTerrainElevation({ lng: longitude, lat: latitude } as Mapbox.LngLat)

    this.edited = position
    this.position.controls[2].setValue(altitude)

    this.toggleEdit("position", this.form, false)
  }

  onFileChange(file: File) {
    if (!file) {
      return
    }

    const feature = this.featureService.selectedFeature
    const image = feature.image

    // Upload file to S3 first, then update/create the feature property with the S3 reference
    this._awsService.uploadFiles([file], { purpose: 'upload-feature-image' }).pipe(
      switchMap(({ token }) => this._awsService.registerS3File(token, 'image', file.size)),
      switchMap(s3Ref => {
        if (image?.id) {
          // Update existing property with the new S3 reference
          image.s3ObjectReferenceId = s3Ref.id
          image.s3ObjectReference = s3Ref
          image.fileReference = null
          image.fileReferenceID = null
          image.value = s3Ref.filename
          return this.featureService.updateFeatureProperty(image).pipe(
            map(() => feature)
          )
        } else {
          // Create a new image property with S3 reference
          const newProp = new FeatureProperty('image', 'file', s3Ref.filename, {
            featureID: feature.id,
            s3ObjectReferenceId: s3Ref.id,
          })
          return this.featureService.createFeatureProperty(newProp).pipe(
            map(() => feature)
          )
        }
      })
    ).subscribe(() => {
      // Re-fetch the updated feature from the store (updateFeatureProperty/createFeatureProperty
      // already called updateFeaturesLocally). Then force the scene to recreate the 3D object
      // so the new image texture is loaded without requiring a page refresh.
      const updatedFeature = this.featureService.features.find(f => f.id === feature.id)
      if (updatedFeature) {
        const sceneManager = this.envManager.sceneManager
        sceneManager.removeFeatureFromScene(updatedFeature)
        this._loadingService.await(sceneManager.loadIn(updatedFeature))
      }
    })
  }

  load() {
    this.envManager.toggleVisibility(this.selectedFeature)
    this.featureService.updateLoadLocally(this.selectedFeature)
    this.featureService.updateFeaturesLocally(this.selectedFeature)

    if (this.unloaded) {
      this._loadingService.await(this.envManager.sceneManager.loadIn(this.selectedFeature))

      this.unloaded = false
    } else {
      this.envManager.sceneManager.loadOut(this.selectedFeature)

      if (this.selectedFeature.modelID) {
        this.envManager.sceneManager.removeFeatureFromScene(this.selectedFeature)
      }

      this.unloaded = true
    }
  }

  copyPosition(feature: Feature) {
    this._copyService.getCoordinates()
      .then(coordinates => {
        coordinates.push([feature.position[0], feature.position[1]])

        const contents = { type: 'coordinates', coordinates } as CoordinatesClipboard
        this._copyService.copyContentsToClipboard(contents)
      })
  }

  pasteCoordinates(feature: Feature) {
    this._copyService.getCoordinates()
      .then(coordinates => {
        this.clipboardCoordinates = coordinates
        if (feature && this.clipboardCoordinates[0]) {
          const [longitude, latitude] = this.clipboardCoordinates[this.clipboardCoordinates.length - 1]
          const position = [longitude, latitude, feature.position[2]] as [number, number, number]

          feature.position = position
          this.updateFeature(feature)
        }
      })
  }

  moveMarker() {
    this._annotationService.enable(this.envManager.modelSpace.canvas, "moving")
  }

  updateAssetPosition() {
    const locationField = this.featuresAsset.fields.find(f => f.type == 'location')
    const locationFieldValue = this.featuresAsset.fieldValues.find(fv => fv.assetFieldID == locationField.id)

    locationFieldValue.value = [this.selectedFeature.position[0], this.selectedFeature.position[1]]

    this._assetService.updateAsset(this.featuresAsset).subscribe()
  }

  /**
   * Handles Enter key press by saving the currently focused input and removing focus
   */
  handleEnterKey(event: KeyboardEvent) {
    event.preventDefault()
    
    const activeElement = document.activeElement as HTMLElement
    if (!activeElement) return
    
    // If the active element is an input, trigger blur to save and remove focus
    if (activeElement.tagName === 'INPUT' || activeElement.tagName === 'TEXTAREA') {
      activeElement.blur()
    }
  }
}